import { Directive, ElementRef, HostListener } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

@Directive({
  selector: '[appTooltipEllipses]',
  providers: [MatTooltip],
  standalone: false
})
export class TooltipEllipsesDirective {
  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly tooltip: MatTooltip
  ) {}

  // mouseenter does not bubble — fires exactly once when the pointer enters
  // this element's own bounding box, never again for child nodes.
  // No setTimeout needed: mouseenter only fires on visible, laid-out elements.
  @HostListener('mouseenter')
  onMouseEnter(): void {
    const el = this.elementRef.nativeElement;

    if (!this.isOverflowing(el)) {
      return;
    }

    // Prefer .value for <input> elements; fall back to innerText for all others.
    const message =
      (el as HTMLInputElement).value?.trim() || el.innerText?.trim();

    if (message) {
      this.tooltip.message = message;
      this.tooltip.show(300);
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.tooltip.hide();

    // Clear stale message so a re-enter on a now-non-overflowing cell
    // cannot accidentally display the previous tooltip text.
    this.tooltip.message = '';
  }

  private isOverflowing(el: HTMLElement): boolean {
    // scrollWidth = actual content width (unclipped, including padding).
    // clientWidth = rendered inner width (padding-box, excludes border).
    //
    // Using clientWidth (not offsetWidth) is important for Angular Material
    // MDC table cells which carry a 1px border-bottom: offsetWidth is 1px
    // larger than clientWidth, causing a 1px blind spot where visually
    // clipped text would be missed by the cruder offsetWidth check.
    //
    // This single comparison works correctly for:
    //   • <td> / <th>  table cells with overflow:hidden + text-overflow:ellipsis
    //   • display:flex cells (Angular Material MDC) — scrollWidth still
    //     reflects the hypothetical un-shrunk content width in modern browsers
    //   • <div class="ellipses"> wrappers used across feature templates
    //   • <input> elements (via the .value path above)
    return el.scrollWidth > el.clientWidth;
  }
}
